import React from 'react';
import { selectInputValueOnFocus } from './simulator/generationProfile/generationProfileInputsUtils';

/**
 * Memoized Capacity Input Component
 * 
 * Extracted from GenerationProfileChart to improve performance.
 * Each input re-renders independently, preventing full component re-renders on every keystroke.
 */
const CapacityInput = React.memo(({
  label,
  icon: Icon,
  iconColor,
  value,
  onChange,
  onGo,
  onEnter,
  loading,
  disabled = false,
  readOnly = false,
  min = "0",
  step = "0.01",
  showGoButton = true,
  inputClassName = "",
  labelPrefix = null,
}) => {
  const DECIMAL_2_REGEX = /^\d*(\.\d{0,2})?$/;

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    if (readOnly) return;
    if (onEnter) {
      e.preventDefault();
      onEnter();
      return;
    }
    if (!isDisabled && onGo) {
      e.preventDefault();
      onGo();
    }
  };

  const handleChange = (e) => {
    if (readOnly) return;
    const next = e.target.value;
    if (next === '' || DECIMAL_2_REGEX.test(next)) {
      onChange(next);
    }
  };

  const num = parseFloat(value);
  const isValidNum = !isNaN(num) && num >= 0;
  const isDisabled = disabled || loading || !isValidNum;

  // Keep labelPrefix (e.g. VO subfuel expander) outside <label>. A <button>
  // inside <label> is labelable and can double-fire click (expand then collapse).
  return (
    <div className="flex items-center justify-between gap-[10px]">
      <div className="label-figma whitespace-nowrap flex items-center gap-1 min-w-0">
        {labelPrefix}
        <label className="flex items-center gap-1 min-w-0">
          {Icon && <Icon className="h-4 w-4 flex-shrink-0" style={{ color: iconColor }} />}
          <span className="truncate">{label}</span>
        </label>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          placeholder="0000.00"
          value={value}
          onChange={handleChange}
          onFocus={readOnly ? undefined : selectInputValueOnFocus}
          onKeyDown={handleKeyDown}
          min={min}
          step={step}
          readOnly={readOnly}
          tabIndex={readOnly ? -1 : undefined}
          className={`input-field-figma [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            readOnly ? 'bg-gray-50 text-gray-600 cursor-default' : ''
          } ${inputClassName}`}
        />
        {showGoButton && (
          <button
            onClick={onGo}
            disabled={isDisabled}
            className="btn-primary text-sm px-3 py-2 disabled:opacity-50"
          >
            {loading ? (
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              'Go'
            )}
          </button>
        )}
      </div>
    </div>
  );
});

CapacityInput.displayName = 'CapacityInput';

export default CapacityInput;

